import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const ROLE_MATRIX = [
  ["Ledgers & masters", "Full", "Full", "Create/Edit", "View", "View"],
  ["Sales invoice", "Full", "Full", "Full", "Create/Edit", "View"],
  ["Purchase bill", "Full", "Full", "Full", "—", "View"],
  ["Bank reconciliation", "Full", "Full", "Full", "—", "View"],
  ["Cancel voucher", "Yes", "Yes", "Yes", "—", "—"],
  ["Reports", "All", "All", "All", "Sales set", "All (read)"],
  ["Team & settings", "Full", "Manage", "—", "—", "—"],
];

export default function Settings() {
  const { user, company } = useApp();
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "accountant" });

  const load = () => api.get("/auth/team").then((r) => setTeam(r.data));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    try {
      await api.post("/auth/team", form);
      toast.success("Team member added");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "accountant" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeader title="Settings & Team" subtitle="Company profile, roles and permission matrix.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" data-testid="invite-user-btn">Add team member</Button></DialogTrigger>
          <DialogContent data-testid="invite-dialog">
            <DialogHeader><DialogTitle className="font-display">Add team member</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {[["Name", "name", "text"], ["Email", "email", "email"], ["Temporary password", "password", "password"]].map(([label, key, type]) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest">{label}</Label>
                  <Input type={type} data-testid={`invite-${key}-input`} value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["owner", "admin", "accountant", "sales", "viewer"].map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={invite} disabled={!form.name || !form.email || form.password.length < 6} data-testid="save-invite-btn">
                Add member
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="panel" data-testid="company-profile">
          <div className="border-b border-border px-4 py-2.5"><h2 className="font-display text-sm font-bold">Company profile</h2></div>
          <table className="dense-table w-full">
            <tbody>
              {[["Legal name", company?.legal_name], ["GSTIN", company?.gstin || "—"], ["PAN", company?.pan || "—"],
                ["State", company?.state || "—"], ["FY start", company?.fy_start], ["Currency", company?.base_currency],
                ["Industry", company?.industry || "—"]].map(([k, v]) => (
                <tr key={k}><td className="w-40 text-muted-foreground">{k}</td><td className="num">{v}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel" data-testid="team-table">
          <div className="border-b border-border px-4 py-2.5"><h2 className="font-display text-sm font-bold">Team</h2></div>
          <table className="dense-table w-full">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th></tr></thead>
            <tbody>
              {team.map((t) => (
                <tr key={t.id} data-testid={`team-row-${t.id}`}>
                  <td className="font-medium">{t.name}{t.id === user?.id ? <span className="ml-2 text-[10px] uppercase text-primary">you</span> : null}</td>
                  <td className="text-muted-foreground">{t.email}</td>
                  <td><span className="bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-widest">{t.role}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel overflow-x-auto" data-testid="permission-matrix">
        <div className="border-b border-border px-4 py-2.5"><h2 className="font-display text-sm font-bold">Permission matrix</h2></div>
        <table className="dense-table w-full min-w-[720px]">
          <thead><tr><th>Capability</th><th>Owner</th><th>Admin</th><th>Accountant</th><th>Sales</th><th>Viewer</th></tr></thead>
          <tbody>
            {ROLE_MATRIX.map((row) => (
              <tr key={row[0]}>
                {row.map((c, i) => <td key={i} className={i === 0 ? "font-medium" : "text-muted-foreground"}>{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
